import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { type PartialCartItem } from '@cart-services/types'
import { type CountryCode, type ProjectCode } from '@constants'
import { useOrderAnalyticsEvent } from '@dni-analysis'
import { ORDER_PLATFORM } from '@dni-analysis/types/order-events'
import { useDialog } from '@dni-hooks/useDialog'
import { useApiECommerceGetPaymentTransactionMutation } from '@hooks-api/eCommerceV2/useApiECommerceGetPaymentTransactionMutation'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { useApiGetPaymentTransactionCancelable } from '@hooks-api/fortune/useApiGetPaymentTransactionCancelable'
import { type ECommerceOrder, type WpOrderStatus, WP_ORDER_STATUS, WP_ORDER_TYPE } from '@hooks-api/types/dataModel/eCommerceOrders'
import { OrderAction } from '@hooks-api/types/dataModel/wp'
import { DIALOG_FAILED_TO_CANCEL_ORDER_KEY, DialogFailedToCancelOrder, type DialogFailedToCancelOrderProps } from '@order-detail/components/shared/Orders/DialogFailedToCancelOrder'
import { RefundStatusBadge } from '@order-detail/components/shared/Orders/RefundStatusBadge'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { ErrorFilled } from '@pets-ui/icons'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { OrderReceipt } from '@wp-my-account/components/OrderReceipt'
import { OrderThumbnail } from '@wp-my-account/components/OrderThumbnail'
import { transformOrderData } from '@wp-my-account/utils/transformOrderDataDTO'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'
import { DialogAddToCartError } from '../../../../fp-b2b-shared/components/DialogAddToCartError/DialogAddToCartError'
import { DIALOG_BUY_AGAIN, DialogBuyAgain, type DialogBuyAgainProps } from '../../../../fp-b2b-shared/components/DialogBuyAgain/DialogBuyAgain'
import { useStmCampaignCode } from '../../../hooks'
import { DIALOG_CONFIRM_CANCEL_TRANSACTION, DialogConfirmCancelTransaction, type DialogConfirmCancelTransactionProps } from './DialogConfirmCancelTransaction'

interface OrderListProps {
  status: WpOrderStatus
  list: ECommerceOrder[]
  basePath: string
  focusedTransactionId?: string | null
  onRepayClick?: () => void
  onCancelOrderClick?: () => void
}

const styles = stylex.create({
  listContainer: {
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: colors.white,
    marginTop: 8,
  },
  orderDetailButton: {
    alignSelf: 'flex-end',
    height: '36px',
  },
  orderTitle: {
    overflow: 'hidden',
    color: colors.grey900,
    fontWeight: 600,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  focusedContainer: {
    borderColor: colors.yellow400,
    borderStyle: 'solid',
    borderWidth: 1,
    scrollMarginTop: '72px',
  },
})

export function OrderList({ status, list, basePath, focusedTransactionId, onRepayClick, onCancelOrderClick }: OrderListProps) {
  const { t } = useTranslation()
  const isTablet = useResponsive('wordPressTablet')
  const { campaignCode, campaignId, projectCode, countryCode } = useStmCampaignCode()
  const transformedOrderData = transformOrderData(list)
  const focusedOrderRef = useRef<HTMLDivElement | null>(null)
  const history = useHistory()

  useEffect(() => {
    if (!focusedTransactionId || !focusedOrderRef.current) return
    focusedOrderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [focusedTransactionId])

  const { openDialog: openConfirmCancelTransactionDialog } = useDialog<DialogConfirmCancelTransactionProps>(DIALOG_CONFIRM_CANCEL_TRANSACTION)
  const { openDialog: openFailedToCancelOrderDialog } = useDialog<DialogFailedToCancelOrderProps>(DIALOG_FAILED_TO_CANCEL_ORDER_KEY)
  const { openDialog: openBuyAgainDialog } = useDialog<DialogBuyAgainProps>(DIALOG_BUY_AGAIN)
  const { trigger: triggerGetPaymentTransaction, isMutating: isMutatingGetPaymentTransaction } = useApiECommerceGetPaymentTransactionMutation()
  const { trigger: triggerGetPaymentTransactionCancelable } = useApiGetPaymentTransactionCancelable()
  const { openSnackbar } = useSnackbar()
  const { destructuredData: campaignMember } = useApiCampaignMember({ code: campaignCode ?? undefined })
  const { sendOrderReorderClickEvent, sendOrderCancelClickEvent, sendOrderCancelSuccessEvent } = useOrderAnalyticsEvent({ userId: campaignMember?.platform_uid ?? '', platform: ORDER_PLATFORM.TO_RESELLER })

  const handleClickOrderDetail = (transactionId?: string | null) => {
    if (!transactionId) return
    history.push(`${basePath}/my-account/order-detail/${transactionId}`)
  }

  const handleOrderActions = async (orderId: string, type: OrderAction, paymentTransactionId: string, orderStatus?: WpOrderStatus) => {
    if (type === OrderAction.CANCEL) {
      const confirmCancelOrShowFailure = async (failureHeader: string, failureContent: string) => {
        try {
          await triggerGetPaymentTransactionCancelable({ id: paymentTransactionId })
          sendOrderCancelClickEvent({ orderId, transactionId: paymentTransactionId, pageSource: status })
          openConfirmCancelTransactionDialog({
            transactionId: paymentTransactionId,
            basePath,
            onCancelSuccess: () => sendOrderCancelSuccessEvent({ orderId, transactionId: paymentTransactionId }),
          })
        }
        catch {
          openFailedToCancelOrderDialog({ header: failureHeader, content: failureContent })
        }
      }

      if (orderStatus === WP_ORDER_STATUS.PENDING) {
        await confirmCancelOrShowFailure(
          '此訂單不支援自行取消',
          '此類付款方式（如 ATM、PayMe、指定付款）不支援自行取消，逾期未付將自動失效。',
        )
      }
      if (orderStatus === WP_ORDER_STATUS.READY_FOR_SHIPMENT) {
        openFailedToCancelOrderDialog({
          header: '商品準備出貨，無法自行取消',
          content: '商品正在進行包裝，此階段已無法取消訂單，如需協助請聯繫業務處理。',
        })
      }
      if (orderStatus === WP_ORDER_STATUS.PROCESSING) {
        await confirmCancelOrShowFailure(
          '此訂單需人工退款，請聯繫業務',
          '此類付款方式（如 ATM、PayMe、指定付款）需由業務執行退費作業，請聯繫業務處理。',
        )
      }
    }
    if (type === OrderAction.REPEAT) {
      try {
        const { data: { data } } = await triggerGetPaymentTransaction({ country_code: countryCode as CountryCode, id: paymentTransactionId })
        const orderItemsWithoutGiveawayAndAddon = data.child_orders
          ?.flatMap(childOrder => childOrder.order_items)
          .filter(item => !item.is_giveaway && !item.is_amount_giveaway)
          .filter(item => !item.is_addon_v2)
        const items: PartialCartItem[] = orderItemsWithoutGiveawayAndAddon?.map(item => ({
          cartItemId: item.variation_id || item.product_id,
          product_id: item.product_id,
          variation_id: Number(item.variation_id),
          quantity: item.quantity,
          sku: item.sku,
          delivery_class: item.delivery_class,
          product_limited: item.product_limited,
          project_code: projectCode as ProjectCode,
          butler: {},
        })).filter(item => !!item.cartItemId) ?? []
        openBuyAgainDialog({ campaignId, items, basePath, platform: Platform.STM })
      }
      catch (error) {
        openSnackbar({
          message: '再次訂購失敗！請重新選購商品或聯繫客服人員',
          icon: <ErrorFilled color={colors.red600} />,
        })
        console.error(error)
      }
    }
    if (type === OrderAction.PAY) {
      onRepayClick?.()
      window.location.href = `${basePath}/retry-payment/${paymentTransactionId}`
    }
  }

  const handleClickOrderAction = async (orderId: string, type: OrderAction, paymentTransactionId?: string | null, orderStatus?: WpOrderStatus) => {
    if (!paymentTransactionId) {
      console.error('[my account] paymentTransactionId not found')
      return
    }
    if (!campaignMember?.account) {
      console.error('[my account] campaign member account not found')
      return
    }
    if (type === OrderAction.REPEAT) {
      sendOrderReorderClickEvent({ orderId, statusTab: status })
    }
    await handleOrderActions(orderId, type, paymentTransactionId, orderStatus)
  }

  return (
    <>
      {transformedOrderData.map((order) => {
        const {
          b2bImages,
          orderId,
          title,
          transactionId,
          isPayable,
          orderStatus,
          refundStatus,
        } = order
        const isCancelledOrder = orderStatus === WP_ORDER_STATUS.CANCELLED
        const isCompletedOrder = orderStatus === WP_ORDER_STATUS.COMPLETED
        const isPendingOrderStatus = orderStatus === WP_ORDER_STATUS.PENDING
        const isProcessingOrReadyForShipment = orderStatus === WP_ORDER_STATUS.PROCESSING || orderStatus === WP_ORDER_STATUS.READY_FOR_SHIPMENT
        const shouldShowCancelOrder = isPendingOrderStatus || isProcessingOrReadyForShipment
        const shouldShowRepeatButton = isCancelledOrder || isCompletedOrder
        const shouldShowRefundStatus = !!refundStatus
        const isFocusedOrder = !!focusedTransactionId && transactionId === focusedTransactionId

        return isTablet
          ? (
            <Box rootRef={isFocusedOrder ? focusedOrderRef : undefined} rootStyle={[styles.listContainer, isFocusedOrder && styles.focusedContainer]} key={orderId}>
              <VStack>
                <HStack flexWrap="nowrap" gap={2}>
                  <Box width="76px" height="76px" minHeight="76px" minWidth="76px">
                    <OrderThumbnail images={b2bImages} />
                  </Box>
                  <VStack width="100%" gap={2} minWidth={0}>
                    <HStack gap={2} alignItems="flex-start" justifyContent="space-between" flexWrap="nowrap">
                      <HStack gap={2} alignItems="center" minWidth={0} flexWrap="nowrap">
                        <Text rootStyle={styles.orderTitle} variant="subtitle">{title}</Text>
                        {order.subtitle && <Text noWrap variant="caption" color={colors.grey600}>{order.subtitle}</Text>}
                      </HStack>
                      {shouldShowCancelOrder && (
                        <Button variant="text" color="blue" onClick={() => handleClickOrderAction(orderId, OrderAction.CANCEL, transactionId, orderStatus)}>
                          <Text noWrap>{t('wpOrder.cancelOrder')}</Text>
                        </Button>
                      )}
                    </HStack>
                    <OrderReceipt order={order} status={status} type={WP_ORDER_TYPE.PAYMENT_TRANSACTION} />
                  </VStack>
                </HStack>
                <HStack justifyContent="space-between" alignItems="center" flexWrap="nowrap">
                  <HStack gap={1} width="100%" alignItems="center">
                    {shouldShowRefundStatus && <RefundStatusBadge refundStatus={refundStatus} isMerchant />}
                  </HStack>
                  <HStack gap={2} width="100%" justifyContent="flex-end">
                    {transactionId && (
                      <Button
                        component="a"
                        href={`${basePath}/my-account/order-detail/${transactionId}`}
                        variant="outlined"
                        color="grey"
                        rootStyle={styles.orderDetailButton}
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault()
                          handleClickOrderDetail(transactionId)
                        }}
                      >
                        {t('wpOrder.orderDetail')}
                      </Button>
                    )}
                    {isPayable && (
                      <Button color="primary" onClick={() => handleClickOrderAction(orderId, OrderAction.PAY, transactionId)}>
                        {t('wpOrder.promptPayment')}
                      </Button>
                    )}
                    {shouldShowRepeatButton && (
                      <Button color="primary" rootStyle={styles.orderDetailButton} onClick={() => handleClickOrderAction(orderId, OrderAction.REPEAT, transactionId, orderStatus)} loading={isMutatingGetPaymentTransaction}>
                        {t('wpOrder.repeatOrder', { defaultValue: '再買一次' })}
                      </Button>
                    )}
                  </HStack>
                </HStack>
              </VStack>
            </Box>
          )
          : (
            <Box key={orderId} rootRef={isFocusedOrder ? focusedOrderRef : undefined} rootStyle={[styles.listContainer, isFocusedOrder && styles.focusedContainer]}>
              <VStack flexWrap="nowrap">
                <HStack flexWrap="nowrap" gap={2} justifyContent="space-between">
                  <Text rootStyle={styles.orderTitle}>{title}</Text>
                  {shouldShowCancelOrder && (
                    <Button variant="text" color="blue" onClick={() => handleClickOrderAction(orderId, OrderAction.CANCEL, transactionId, orderStatus)}>
                      <Text noWrap>{t('wpOrder.cancelOrder')}</Text>
                    </Button>
                  )}
                </HStack>
                <HStack gap={2} mt={2} flexWrap="nowrap" width="100%">
                  <Box minWidth="116px" minHeight="116px" width="116px" height="116px">
                    <OrderThumbnail images={b2bImages} />
                  </Box>
                  <OrderReceipt order={order} status={status} type={WP_ORDER_TYPE.PAYMENT_TRANSACTION} />
                </HStack>
                <HStack mt={1} alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                  <HStack gap={0.5} flexShrink={1}>
                    {shouldShowRefundStatus && <RefundStatusBadge refundStatus={refundStatus} isMerchant />}
                  </HStack>
                  <HStack gap={2} justifyContent="flex-end" flexShrink={0}>
                    {transactionId && (
                      <Button variant="outlined" color="grey" rootStyle={styles.orderDetailButton} onClick={() => handleClickOrderDetail(transactionId)}>
                        {t('wpOrder.orderDetail')}
                      </Button>
                    )}
                    {isPayable && (
                      <Button color="primary" onClick={() => handleClickOrderAction(orderId, OrderAction.PAY, transactionId)}>
                        {t('wpOrder.promptPayment')}
                      </Button>
                    )}
                    {shouldShowRepeatButton && (
                      <Button color="primary" rootStyle={styles.orderDetailButton} onClick={() => handleClickOrderAction(orderId, OrderAction.REPEAT, transactionId, orderStatus)} loading={isMutatingGetPaymentTransaction}>
                        {t('wpOrder.repeatOrder')}
                      </Button>
                    )}
                  </HStack>
                </HStack>
              </VStack>
            </Box>
          )
      })}
      <DialogConfirmCancelTransaction onCancelOrderClick={onCancelOrderClick} />
      <DialogFailedToCancelOrder />
      <DialogBuyAgain />
      <DialogAddToCartError />
    </>
  )
}
